import { gql, useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { ADD_STOP } from '../../../../../data/mutations/stops'
import { MY_SHIPMENT } from '../../../../../data/queries/shipment'
import type { ISearchResultDTO } from '../../../../../types/stop'
import { updateCacheFieldWithNewData } from '../../../../../utils/graphql-cache'
import { AddStop } from './add-stop'
import { useStops } from './contexts/use-stops-context'
import { generateStopDetailsFromSearchResult } from './utils'

/*
 * This component is used to add a stop to the shipment.
This container is used to handle business logic for adding a stop to the shipment.
 */
const AddStopContainer = () => {
  const { shipment } = useStops()

  const [addStop] = useMutation(ADD_STOP, {
    onCompleted: () => {
      toast.success('Stop added successfully')
    },
    onError: (error) => {
      toast.error(error.message)
    },
    refetchQueries: [
      {
        query: MY_SHIPMENT,
        variables: { id: shipment.id }
      }
    ],
    update: updateCacheFieldWithNewData({
      cacheFieldKey: 'stops',
      entityID: shipment.id,
      entityTypeName: 'Shipment',
      fragment: gql`
        fragment ExistingStops on Shipment {
          id
          stops {
            id
            warehouse {
              name
              category
              rating
              reviews
              placeID
              thumbnail
              warehouseHours
              address {
                addressLabel
                streetNumber
                street
                city
                state
                stateCode
                county
                postalCode
                postalCodeSuffix
                country
                countryCode
                coordinates {
                  latitude
                  longitude
                }
              }
            }
            appointment {
              start {
                date
                time
              }
              end {
                date
                time
              }
              isFirstComeFirstServe
            }
            referenceNumber
            type
            sequence
            instructions
            eta {
              date
              time
            }
            arrivalDateTime {
              date
              time
            }
            departureDateTime {
              date
              time
            }
          }
        }
      `,
      mutationResponseKey: 'stop'
    })
  })

  const handleAddStop = (data: ISearchResultDTO) => {
    const stop = generateStopDetailsFromSearchResult(data, shipment.stops?.length ?? 0)

    // Call the mutation to add the stop
    const input = {
      id: stop.id,
      sequence: stop.sequence,
      shipmentID: shipment.id,
      type: stop.type,
      warehouse: {
        ...Object.fromEntries(Object.entries(stop.warehouse ?? {}).filter(([, v]) => v != null)),
        address: Object.fromEntries(Object.entries(stop?.warehouse?.address ?? {}).filter(([, v]) => v != null))
      }
    }

    toast.info('Adding stop...')
    addStop({ context: { headers: { 'ws-id': shipment.shipmentOwnerWorkspace?.id } }, variables: input })
  }

  return <AddStop onAddStopAction={handleAddStop} />
}

export default AddStopContainer
